import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import { ExpandMore, OpenInNew, ReceiptLong, Refresh } from '@mui/icons-material'
import type { PedidoMeu, PedidoDadosPagamento, Pacote } from '../types'
import { apiService } from '../services/api'
import { WARZONE_COIN_IMAGE_URL } from '../properties'

const BRAZIL_TIMEZONE = 'America/Sao_Paulo'
const BACKEND_IMPLICIT_OFFSET = '-06:00'

const toBrazilDateTime = (value: string | null | undefined) => {
  if (!value) return '—'
  const raw = String(value).trim()
  const hasExplicitTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(raw)
  const iso = hasExplicitTimezone ? raw : `${raw}${BACKEND_IMPLICIT_OFFSET}`
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('pt-BR', { timeZone: BRAZIL_TIMEZONE })
}

const formatBRL = (value: number | null | undefined) => {
  const v = Number(value ?? 0)
  if (Number.isNaN(v)) return '—'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const formatPaymentType = (tipo: string | undefined | null) => {
  const t = String(tipo ?? '').toUpperCase()
  if (!t) return '—'
  if (t === 'PIX_DIRETO') return 'PIX'
  return t.split('_').join(' ')
}

const statusColor = (status: string | undefined | null) => {
  const s = String(status ?? '').toUpperCase()
  if (['APROVADO', 'PAGO', 'CONFIRMADO'].includes(s)) return 'success' as const
  if (['PENDENTE', 'AGUARDANDO', 'EM_PROCESSAMENTO'].includes(s)) return 'warning' as const
  if (['CANCELADO', 'RECUSADO', 'ERRO', 'FALHOU'].includes(s)) return 'error' as const
  return 'default' as const
}

const PackagePill: React.FC<{ pacote: Pacote }> = ({ pacote }) => {
  const hasImage = !!pacote.urlImagem
  const points = Number(pacote.pontos ?? 0) || 0
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.25,
        borderRadius: 2.5,
        border: '1px solid rgba(255,255,255,0.10)',
        bgcolor: 'rgba(255,255,255,0.04)'
      }}
    >
      <Avatar
        variant="rounded"
        src={hasImage ? pacote.urlImagem! : undefined}
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)'
        }}
      >
        {String(pacote.nome ?? '?')[0]?.toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }} noWrap>
          {pacote.nome}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.2 }} noWrap>
          {formatBRL(pacote.preco)}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end">
        {pacote.vip && (
          <Chip
            size="small"
            label={`VIP ${Number(pacote.diasVip ?? 0) || 0}d`}
            variant="outlined"
            sx={{ fontWeight: 900, borderColor: 'rgba(255,109,0,0.35)' }}
          />
        )}
        {pacote.warzoneCoin && (
          <Chip
            size="small"
            label={points ? `${points.toLocaleString('pt-BR')} coins` : 'Coins'}
            variant="outlined"
            icon={
              <Box
                component="img"
                src={WARZONE_COIN_IMAGE_URL}
                alt="Coin"
                sx={{ width: 14, height: 14, opacity: 0.95 }}
                loading="lazy"
              />
            }
            sx={{ fontWeight: 900, borderColor: 'rgba(0,230,118,0.35)' }}
          />
        )}
      </Stack>
    </Box>
  )
}

const getPaidAt = (dados: PedidoDadosPagamento | null | undefined) => (dados?.horarioPagamento ? toBrazilDateTime(dados.horarioPagamento) : null)

const MeusPedidosPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [orders, setOrders] = useState<PedidoMeu[]>([])
  const [packagesOrder, setPackagesOrder] = useState<PedidoMeu | null>(null)
  const [expandedId, setExpandedId] = useState<string | false>(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiService.getMeusPedidos({ game: 'palworld' })
      setOrders(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setError(e?.message || 'Erro ao consultar seus pedidos.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.getMeusPedidos({ game: 'palworld' })
        if (!mounted) return
        setOrders(Array.isArray(data) ? data : [])
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar seus pedidos.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }
    void run()
    return () => {
      mounted = false
    }
  }, [])

  const summary = useMemo(() => {
    const total = orders.length
    const aprovados = orders.filter((o) => String(o.status).toUpperCase() === 'APROVADO').length
    const totalGasto = orders
      .filter((o) => ['APROVADO', 'PAGO', 'CONFIRMADO'].includes(String(o.status).toUpperCase()))
      .reduce((acc, o) => acc + (Number(o.valorTotal ?? 0) || 0), 0)
    return { total, aprovados, totalGasto }
  }, [orders])

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptLong />
              Meus Pedidos
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>Histórico de pedidos e detalhes do pagamento.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ p: { xs: 2.25, md: 2.75 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Pedidos
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{summary.total}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Aprovados
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{summary.aprovados}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Total gasto
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{formatBRL(summary.totalGasto)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading && orders.length === 0 ? (
          <Box sx={{ minHeight: '35dvh', display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontWeight: 900 }}>Nenhum pedido encontrado.</Typography>
              <Typography sx={{ color: 'text.secondary' }}>Quando você finalizar uma compra, ela aparece aqui.</Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {orders.map((o) => (
              <Accordion
                key={o.id}
                expanded={expandedId === o.id}
                onChange={(_, isExpanded) => setExpandedId(isExpanded ? o.id : false)}
                disableGutters
                square={false}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.10)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  backgroundImage:
                    'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 70%)',
                  '&:before': { display: 'none' },
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 0.5,
                    '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ flex: 1 }}>
                      <Chip
                        size="small"
                        label={String(o.status ?? '—').toUpperCase()}
                        color={statusColor(o.status)}
                        variant="outlined"
                        sx={{ fontWeight: 900 }}
                      />
                      <Chip
                        size="small"
                        label={formatPaymentType(o.tipoPagamento)}
                        variant="outlined"
                        sx={{ fontWeight: 900, borderColor: 'rgba(255,255,255,0.16)' }}
                      />
                    </Stack>

                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {getPaidAt(o.dadosPagamento) ?? '—'}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total
                      </Typography>
                      <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{formatBRL(o.valorTotal)}</Typography>
                      {Number(o.valorDesconto ?? 0) > 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Desconto: {formatBRL(o.valorDesconto)}
                        </Typography>
                      ) : null}
                      {o.cupomCodigo ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          Cupom: <strong>{o.cupomCodigo}</strong>
                        </Typography>
                      ) : null}
                    </Box>

                    {o.linkVisualizacao ? (
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<OpenInNew />}
                        component="a"
                        href={o.linkVisualizacao}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Ver cobrança
                      </Button>
                    ) : null}
                  </Box>

                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Pacotes
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {(o.pacotes ?? []).slice(0, 2).map((p) => p.nome).filter(Boolean).join(' • ') || '—'}
                        {(o.pacotes?.length ?? 0) > 2 ? ` • +${(o.pacotes?.length ?? 0) - 2}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPackagesOrder(o)}
                      sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      Ver pacotes
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={!!packagesOrder}
        onClose={() => setPackagesOrder(null)}
        maxWidth="md"
        fullWidth
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.75)' } }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(7, 8, 18, 0.96)',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.10)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Pacotes</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {(packagesOrder?.pacotes?.length ?? 0).toLocaleString('pt-BR')} item(ns) nesta compra.
          </Typography>
          <Grid container spacing={1.25}>
            {(packagesOrder?.pacotes ?? []).map((p) => (
              <Grid key={p.id} item xs={12} md={6}>
                <PackagePill pacote={p} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackagesOrder(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MeusPedidosPage
